export type Volume = {
  icon: string
  number: string
  title: string
  subtitle: string
  popular?: boolean
  problem: string
  bullets: string[]
  result: string
}

export const volumes: Volume[] = [
  {
    icon: '📗',
    number: 'Volume 1 — 25 Lessons',
    title:
      'Fundamental movement, safety, positioning and guard foundations',
    subtitle:
      'The foundation your students need to feel safe, supported and prepared from day one',
    popular: true,
    problem:
      'Teaching absolute beginners is one of the most rewarding — and most demanding — parts of being a BJJ instructor. This volume gives you everything you need to guide new students with clarity, patience and structure, without improvising a single cue.',
    bullets: [
      'How to introduce fundamental movement patterns — rolling, falling, ground movement, bridging and shrimping from the very first session',
      'How to teach positioning and body awareness — balance, weight distribution, hip control, base position and support points',
      'How to sequence safety instruction — tap protocol, mat etiquette, communication during controlled training and injury prevention',
      'How to teach closed and open guard — guard structure, hip control, basic retention and responses to the passer',
      'How to introduce top positional control — mount, side control, body structure, weight distribution and pressure points',
    ],
    result:
      'Your beginners feel supported, capable and motivated to come back — from their very first class.',
  },
  {
    icon: '📘',
    number: 'Volume 2 — 25 Lessons',
    title: 'Escapes, defensive skills and guard passing fundamentals',
    subtitle: 'Structured, progressive and ready to teach at every step',
    problem:
      'Escapes and guard passing are the most requested — and most difficult — areas to teach consistently week after week. This volume gives you 25 fully structured sessions with varied technical approaches, so every lesson feels intentional, progressive and complete.',
    bullets: [
      'How to develop specific escape fundamentals — bridge and roll, elbow-knee escape, hip escape and variations from mount and side control',
      'How to teach escapes from control positions — back escape, turtle position recovery and choke defense',
      'How to sequence guard passing — toreando, knee slice, stack pass and pressure passes with clean transitions',
      'How to build consistency progressions — timing drills, reaction training, feedback loops and corrections for the most common errors',
      'How to prepare students for positional training — structuring controlled resistance, introducing positional sparring and evaluating technical execution',
    ],
    result:
      'You teach escapes and guard passing with confidence and consistency — and your students feel clear and measurable progress every single week.',
  },
  {
    icon: '📙',
    number: 'Volume 3 — 25 Lessons',
    title: 'Positional control, transitions and takedown fundamentals',
    subtitle:
      'Technical with intention — fully guided from the first drill to the last progression',
    problem:
      'Teaching positional control and takedowns demands precise cues, smooth progressions and a clear sense of progression. This volume removes all planning pressure and gives you sessions that flow naturally from setup to full execution.',
    bullets: [
      'How to teach advanced positional control — back control, north-south, knee on belly, pressure adjustments and anchor points',
      'How to develop clean transitions — side control to mount, mount to back, guard recovery and linking movements without losing control',
      'How to introduce takedown fundamentals — single leg, double leg, hip throw, trip takedowns and safe falling from standing',
      'How to work takedown defense — clinch position, sprawl, framing and guard recovery from standing',
      'How to build longer positional sequences — linking control, transition and submission in cohesive training scenarios',
    ],
    result:
      'Your students gain real confidence in positional control and takedowns — and you deliver those lessons with a complete, structured plan every time.',
  },
  {
    icon: '📕',
    number: 'Volume 4 — 25 Lessons',
    title:
      'Essential submissions, advanced technical development, assessment and annual planning',
    subtitle:
      'The sessions your developing students need — and the hardest ones to organize well',
    problem:
      'Teaching submissions and developing intermediate students requires a completely different type of preparation: attack chains, strategic thinking, technical development management and full annual planning. This volume gives you everything structured so you can focus completely on your students.',
    bullets: [
      'How to teach essential submissions — armbar, triangle choke, rear naked choke, guillotine, kimura, americana and their variations from different positions',
      'How to develop attack chains — combining submissions with sweeps, transition entries and responses to the opponent’s defense',
      'How to guide controlled sparring training — structuring positional rounds, setting training objectives and building technical resilience',
      'How to conduct initial, monthly and final assessments — and organize complete training cycles of 4, 8 and 12 weeks across a full year',
      'How to distribute the 100 lessons across technical weeks, review weeks, assessment weeks, positional sparring weeks and competition preparation weeks',
    ],
    result:
      'Your developing students grow as complete practitioners — and you lead that process with a fully professional structure.',
  },
]
